import { FileReader } from '@/platform'
import FileItem from './FileItem'
import FileItemBuf from './FileItemBuf'
import GameStr from './GameStr'
import GameUnite from './GameUnite'
import GameVar from './GameVar'
import type MainProject from './MainProject'
import SystemData from './SystemData'
import GameMallLimiteModel from './MallLimiteClass/GameMallLimiteModel'
import GameMallNewModel from './MallNewClass/GameMallNewModel'
import WelfareModel from './Welfare/WelfareModel'
import { parseLegacyOuiSystemDataInto, resetSystemData } from './legacyOuiSystemData'

function createGameMallNewTarget(target: Pick<MainProject, 'systemData' | 'Width' | 'Height'>): GameMallNewModel {
  return new GameMallNewModel(target.systemData.totalSetting.FontColor_UI, {
    Width: target.Width,
    Height: target.Height,
  })
}

export function readLegacyOuiPayloadIntoTarget(
  target: MainProject,
  reader: FileReader,
  dataVersion: number,
  isNew = false,
): FileItem[] {
  const files: FileItem[] = []
  target.UiName = reader.readString()
  target.Bitmap = reader.readSome(reader.readInt())
  target.Width = reader.readInt()
  target.Height = reader.readInt()

  const sysLen = Math.max(0, reader.readInt())
  const sysBytes = reader.readSome(sysLen)
  try {
    parseLegacyOuiSystemDataInto(target.systemData, sysBytes)
  } catch {
    resetSystemData(target.systemData)
    target.systemData.readVersionedOui(new FileReader(sysBytes), dataVersion)
  }

  const filesLen = Math.max(0, reader.readInt())
  for (let index = 0; index < filesLen; index += 1) {
    const fileItem = new FileItem()
    fileItem.readVersionedOui(reader.readObj(reader.readInt()))
    files.push(fileItem)
  }

  target.gameVar = new GameVar()
  target.gameVarEx = new GameVar(true)
  target.gameStr = new GameStr()
  if (!reader.isReadEnd()) {
    target.gameVar.readVersionedOui(reader.readObj(reader.readInt()))
    target.gameVarEx.readVersionedOui(reader.readObj(reader.readInt()))
    target.gameStr.readVersionedOui(reader.readObj(reader.readInt()))
  }

  target.GameMallNew = null
  if (!reader.isReadEnd()) {
    const mallBytes = reader.readSome(Math.max(0, reader.readInt()))
    if (mallBytes.length) {
      try {
        target.GameMallNew = createGameMallNewTarget(target)
        target.GameMallNew.readVersionedOui(new FileReader(mallBytes), dataVersion, !isNew)
      } catch {
        target.GameMallNew = null
      }
    }
  }

  target.gameUnite = new GameUnite()
  if (!reader.isReadEnd()) {
    target.gameUnite.readVersionedOui(reader.readObj(reader.readInt()))
  }

  target.GameMallLimite = new GameMallLimiteModel()
  if (!reader.isReadEnd()) {
    target.GameMallLimite.readVersionedOui(reader.readObj(reader.readInt()), !isNew)
    target.GameMallLimite.LimitTime = false
  }

  target.Welfare = new WelfareModel()
  if (!reader.isReadEnd()) {
    target.Welfare.readVersionedOui(reader.readObj(reader.readInt()))
  }

  target.FilesBuf = []
  return files
}

export const readLegacyOuiIntoTarget = readLegacyOuiPayloadIntoTarget

export function readVersionedOuiPayloadIntoTarget(
  target: MainProject,
  reader: FileReader,
  dataVersion: number,
  isNew = false,
): FileItem[] {
  target.UiName = reader.readString()
  target.Bitmap = reader.readSome(reader.readInt())
  target.Width = reader.readInt()
  target.Height = reader.readInt()

  resetSystemData(target.systemData)
  target.systemData.readVersionedOui(reader.readObj(reader.readInt()), dataVersion)

  target.gameVar = new GameVar()
  target.gameVarEx = new GameVar(true)
  target.gameStr = new GameStr()
  if (!reader.isReadEnd()) {
    target.gameVar.readVersionedOui(reader.readObj(reader.readInt()))
    target.gameVarEx.readVersionedOui(reader.readObj(reader.readInt()))
    target.gameStr.readVersionedOui(reader.readObj(reader.readInt()))
  }

  target.GameMallNew = null
  if (!reader.isReadEnd()) {
    try {
      target.GameMallNew = createGameMallNewTarget(target)
      target.GameMallNew.readVersionedOui(reader, dataVersion, !isNew)
    } catch {
      target.GameMallNew = null
    }
  }

  target.gameUnite = new GameUnite()
  if (!reader.isReadEnd()) {
    target.gameUnite.readVersionedOui(reader.readObj(reader.readInt()))
  }

  target.GameMallLimite = new GameMallLimiteModel()
  if (!reader.isReadEnd()) {
    target.GameMallLimite.readVersionedOui(reader, !isNew)
    target.GameMallLimite.LimitTime = false
  }

  target.Welfare = new WelfareModel()
  if (!reader.isReadEnd()) {
    target.Welfare.readVersionedOui(reader)
  }

  const fileItems: FileItem[] = []
  const filesBufLen = reader.isReadEnd() ? 0 : Math.max(0, reader.readInt())
  target.FilesBuf = []
  for (let index = 0; index < filesBufLen; index += 1) {
    const fileBuf = new FileItemBuf()
    fileBuf.readData(reader.readObj(reader.readInt()))
    target.FilesBuf.push(fileBuf)
    fileItems.push(new FileItem(fileBuf.name, fileBuf.content.length, 0))
  }

  return fileItems
}

export const readVersionedOuiIntoTarget = readVersionedOuiPayloadIntoTarget

